import { z } from 'zod';
import {
  colleagueSchema,
  stockUsageRowSchema,
  storeroomWorkerSchema,
  workerPinResultSchema,
  type ColleagueDto,
  type CreateStoreroomWorkerInput,
  type StoreroomWorkerDto,
  type UpdateStoreroomWorkerInput,
  type WorkerPinResultDto,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

export const usagePageSchema = z.object({
  items: z.array(stockUsageRowSchema),
  total: z.number(),
  page: z.number(),
  limit: z.number(),
});

export type UsagePage = z.infer<typeof usagePageSchema>;

export interface UsageQuery {
  storeroomId?: string;
  workerId?: string;
  /** Bitta LOT bo'yicha — «bu mahsulotni kim olgan?» degan savol uchun. */
  lotId?: string;
  page?: number;
  limit?: number;
}

export interface StorekeepingApi {
  workers: () => Promise<StoreroomWorkerDto[]>;
  colleagues: (search: string) => Promise<ColleagueDto[]>;
  createWorker: (input: CreateStoreroomWorkerInput) => Promise<WorkerPinResultDto>;
  updateWorker: (id: string, input: UpdateStoreroomWorkerInput) => Promise<StoreroomWorkerDto>;
  deleteWorker: (id: string) => Promise<void>;
  resetPin: (id: string) => Promise<WorkerPinResultDto>;
  usages: (query?: UsageQuery) => Promise<UsagePage>;
  lotLabel: (lotId: string) => Promise<Blob>;
  requisitionLabels: (requisitionId: string) => Promise<Blob>;
}

const json = (body: unknown): RequestInit => ({ method: 'POST', body: JSON.stringify(body) });

export const createStorekeepingApi = (client: ApiClient): StorekeepingApi => ({
  workers: () => client.apiFetch('/subdivisions/mine/workers', { schema: z.array(storeroomWorkerSchema) }),

  colleagues: (search) =>
    client.apiFetch(`/subdivisions/mine/colleagues?${new URLSearchParams({ search }).toString()}`, {
      schema: z.array(colleagueSchema),
    }),

  createWorker: (input) =>
    client.apiFetch('/subdivisions/mine/workers', { ...json(input), schema: workerPinResultSchema }),

  updateWorker: (id, input) =>
    client.apiFetch(`/subdivisions/mine/workers/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(input),
      schema: storeroomWorkerSchema,
    }),

  deleteWorker: async (id) => {
    await client.apiFetch(`/subdivisions/mine/workers/${id}`, { method: 'DELETE' });
  },

  resetPin: (id) =>
    client.apiFetch(`/subdivisions/mine/workers/${id}/pin`, { method: 'POST', schema: workerPinResultSchema }),

  usages: (query = {}) => {
    const params = new URLSearchParams({ page: String(query.page ?? 1), limit: String(query.limit ?? 30) });
    if (query.storeroomId !== undefined) params.set('storeroomId', query.storeroomId);
    if (query.workerId !== undefined) params.set('workerId', query.workerId);
    if (query.lotId !== undefined) params.set('lotId', query.lotId);
    return client.apiFetch(`/subdivisions/mine/usages?${params.toString()}`, { schema: usagePageSchema });
  },

  lotLabel: (lotId) => client.apiBlob(`/subdivisions/mine/lots/${lotId}/label`),

  requisitionLabels: (requisitionId) => client.apiBlob(`/requisitions/${requisitionId}/lot-labels`),
});

export const storekeepingApi = createStorekeepingApi(apiClient);
